import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { extractApiErrorMessage } from 'utils/helpers/apiError';
import MaterialCategoryAPI from 'api/material-category.api';
import { type MaterialCategoryRecord } from './types';

interface Props {
  open: boolean;
  initialCode?: string;
  onClose: () => void;
  onCreated: (created: MaterialCategoryRecord) => void;
}

const sanitizeCode = (raw: string): string =>
  raw.toUpperCase().replace(/[^A-Z0-9_-]/g, '');

export const AddMaterialCategoryModal = ({
  open,
  initialCode,
  onClose,
  onCreated,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const materialCategoryApi = useRef(new MaterialCategoryAPI()).current;
  const [enumValue, setEnumValue] = useState('');
  const [category, setCategory] = useState('');
  const [errors, setErrors] = useState<{
    enumValue?: string;
    category?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setEnumValue(sanitizeCode(initialCode ?? ''));
      setCategory('');
      setErrors({});
    }
  }, [open, initialCode]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const validate = (): boolean => {
    const next: { enumValue?: string; category?: string } = {};
    if (!enumValue.trim())
      next.enumValue = t('material_category_field_required');
    if (!category.trim()) next.category = t('material_category_field_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const created = await materialCategoryApi.create({
        enumValue: enumValue.trim(),
        category: category.trim(),
      });
      onCreated(created);
    } catch (e) {
      const message = extractApiErrorMessage(
        e,
        t('material_category_toast_create_failed')
      );
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <CategoryOutlinedIcon
              sx={{ fontSize: 18, color: 'primary.main' }}
            />
          </Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {t('material_category_add_title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('material_category_field_code')}
              fullWidth
              size="small"
              autoFocus
              value={enumValue}
              onChange={(e) => {
                setEnumValue(sanitizeCode(e.target.value));
              }}
              error={!!errors.enumValue}
              helperText={
                errors.enumValue ?? t('material_category_code_helper')
              }
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('material_category_field_category')}
              fullWidth
              size="small"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
              }}
              error={!!errors.category}
              helperText={errors.category}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={submitting}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('material_category_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('material_category_btn_add_submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
